<template>
    <div
        class="tw-my-8"
    >
        <Heading
            :title="isAsiInquiry ? 'Mit welchem Budget dürfen wir planen?' : 'Mit welchem Budget dürfen wir Deine Reiseplanung starten?'"
            :size="isAsiInquiry ? 2 : 3"
            :class="{ '!tw-text-[32px] !tw-leading-[1.1] !tw-mt-20': isAsiInquiry }"
        />
        <info-snippet :hasBackground="!isAsiInquiry">
            Für eine {{ travelData.duration }}-tägige Reise in {{ countryName }} empfehlen unsere lokalen Experten ein Budget von {{ advisedBudget?.toLocaleString('de-DE') }}€ pro Person.
            <span v-if="!isAsiInquiry">Dein Budget kommt der lokalen Wirtschaft zugute und trägt zur Wertschöpfung vor Ort bei.</span>
        </info-snippet>
        <vue-slider
            class="tw-mx-4 tw-mt-8"
            v-model="sliderValue"
            :lazy="true"
            :interval="100"
            :min="min"
            :max="max"
            :dot-size="25"
            :process-style="{ backgroundColor: '#979797' }"
            :dot-style="{ backgroundColor: 'var(--brand-primary)', boxShadow: '0 0 0 0' }"
            :tooltip-style="{ backgroundColor: 'var(--brand-primary)', borderColor: 'var(--brand-primary)'}"
            :tooltip-formatter="value => value.toLocaleString('de-DE') + '€' + (value === max ? '+' : '')"
        />
        <div class="tw-mt-2">
            {{ sliderValue[0].toLocaleString('de-DE') }}€ - {{ sliderValue[1].toLocaleString('de-DE') }}€{{ sliderValue[1] === max ? '+' : '' }} pro Person ohne Flug
        </div>
    </div>
</template>

<script>
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/default.css";
import { mapGetters } from "vuex";
import InfoSnippet from "./InfoSnippet.vue";

export default {
    name: "BudgetSelector",
    components: {
        InfoSnippet,
        VueSlider
    },
    data: () => ({
        sliderValue: [0, 100],
        min: 0,
        max: 100000,
    }),
    computed: {
        ...mapGetters({
            tour: 'tour/route',
            country: 'tour/country',
            travelData: 'tour/travelData',
            advisedBudget: 'tour/advisedBudget',
            isAsiInquiry: 'tour/isAsiInquiry',
        }),
        countryName() {
            return this.country?.title || this.tour.mainCountry.staticCountry.shortName;
        }
    },
    mounted() {
        const budget = this.$store.getters['tour/travelData'].budget;
        if(budget) {
            // Handle case where budget comes as a string
            if (typeof budget === 'string') {
                // Check if it's a comma-separated format like "2900, 3400"
                if (budget.includes(',')) {
                    const values = budget.split(',').map(val => parseFloat(val.trim()));
                    if (values.length >= 2 && values.every(val => !isNaN(val))) {
                        this.sliderValue = [values[0], values[1]];
                    } else if (values.length === 1 && !isNaN(values[0])) {
                        // Single value case
                        this.sliderValue = [values[0], values[0]];
                    }
                } else {
                    // Try JSON parsing for other string formats
                    try {
                        this.sliderValue = JSON.parse(budget);
                    } catch (e) {
                        console.error('Error parsing budget:', e);
                    }
                }
            } else {
                this.sliderValue = budget;
            }
        }
        this.updateBudgetValues();
    },
    watch: {
        sliderValue: function (val) {
            this.$store.commit('tour/setBudget', val);
        },
        "$store.state.tour.travelData.duration": {
            handler: function () {
                this.updateBudgetValues()
            },
            deep: true,
        },
        "$store.state.tour.travelData.month": {
            handler: function () {
                this.updateBudgetValues()
            },
            deep: true,
        },
        "$store.state.tour.travelData.groupSize": {
            handler: function () {
                this.updateBudgetValues()
            },
            deep: true,
        },
    },
    methods: {
        calculateBudgetValues() {
            const monthsShort = [
                "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
            ];
            if (!this.travelData) {
                return { budgetPerDay: 0, minimumBudgetPerDay: 0 };
            }
            const duration = this.travelData.duration;
            const countryData = this.country?.mainCountry || this.tour?.mainCountry;
            let budgetPerDay, minimumBudgetPerDay;

            // if tour has budget values, use them otherwise use the country values
            if(this.tour?.routeAdvisedBudgetPerDay && this.tour?.routeMinimumBudgetPerDay) {
                budgetPerDay = this.tour.routeAdvisedBudgetPerDay;
                minimumBudgetPerDay = this.tour.routeMinimumBudgetPerDay;
            } else if (countryData) {
                const selectedMonth = monthsShort[this.travelData.month - 1];

                if (selectedMonth && countryData.advisedBudgetPerDay[selectedMonth]) {
                    budgetPerDay = countryData.advisedBudgetPerDay[selectedMonth];
                    minimumBudgetPerDay = countryData.minimumBudgetPerDay[selectedMonth];
                } else {
                    budgetPerDay = countryData.advisedBudgetPerDay.default;
                    minimumBudgetPerDay = countryData.minimumBudgetPerDay.default;
                }
            } else {
                return { budgetPerDay: 0, minimumBudgetPerDay: 0 };
            }

            // add soloTravelerFactor
            const totalPeople = this.travelData.groupSize.adults + 
                                this.travelData.groupSize.children + 
                                this.travelData.groupSize.infants;
            if (totalPeople === 1 && countryData?.travelingAloneFactor) {
                budgetPerDay *= (countryData.travelingAloneFactor || 1);
                minimumBudgetPerDay *= (countryData.travelingAloneFactor || 1);
            }

            return { budgetPerDay, minimumBudgetPerDay };
        },
        updateBudgetValues() {
            const { budgetPerDay, minimumBudgetPerDay } = this.calculateBudgetValues();
            
            if (!this.travelData) {
                return;
            }
            
            const duration = this.travelData.duration;

            // Calculate and update advisedBudget in store
            const calculatedAdvisedBudget = budgetPerDay > 0 ? Math.ceil(budgetPerDay * duration / 100) * 100 : null;
            this.$store.commit('tour/setAdvisedBudget', calculatedAdvisedBudget);

            const newValues = {
                min: Math.ceil(minimumBudgetPerDay * duration / 100) * 100,
                max: Math.ceil(budgetPerDay * duration * 2 / 100) * 100,
            };

            const defaultLower = Math.ceil(budgetPerDay * duration / 100) * 100;
            const defaultUpper = Math.ceil(budgetPerDay * duration * 1.2 / 100) * 100;

            if (
                this.sliderValue[0] === 0 && this.sliderValue[1] === 100 ||
                this.sliderValue[0] < newValues.min ||
                this.sliderValue[1] > newValues.max
            ) {
                newValues.sliderValue = [defaultLower, defaultUpper];
            } else {
                newValues.sliderValue = [...this.sliderValue];
            }

            // Update all values simultaneously
            Object.assign(this, newValues);
        }
    }
}
</script>